import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import axiosInstance, { getAPIURL } from "../axiosInstance";
import { Users } from "../../types/Types";

type UpdateUserDto = Partial<Users>;

export const useSaveUser = (
  options?: UseMutationOptions<
    Users,
    unknown,
    Users | UpdateUserDto | FormData,
    unknown
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (data: Partial<Users> | FormData) => {
      const isFormData = data instanceof FormData;

      // Determine if it's an update or create operation
      let hasId = isFormData ? data.get("id") ?? null : (data as Partial<Users>).user?.id ?? null;

      // Membership-upgrade path can miss `id` but still has objectId for an existing row.
      // Resolve user id from objectId so we hit Update instead of Create.
      if (!hasId) {
        const objectId = isFormData
          ? data.get("objectId")
          : (data as Partial<Users>).user?.objectId;

        if (typeof objectId === "string" && objectId.trim()) {
          try {
            const existing = await axiosInstance.get(
              getAPIURL(`/User/ByObjectId/${encodeURIComponent(objectId.trim())}`)
            );
            const existingId =
              existing?.data?.user?.id ?? existing?.data?.User?.id ?? null;
            if (existingId) {
              hasId = existingId;
              if (isFormData) data.set("id", String(existingId));
            }
          } catch {
            // No existing user row by objectId; create flow continues as-is.
          }
        }
      }

      // Let the browser set multipart boundary. A hard-coded Content-Type
      // (or the instance default application/json) breaks IFormFile binding.
      const config = isFormData
        ? { headers: { "Content-Type": undefined as unknown as string } }
        : {};

      if (hasId) {
        return axiosInstance
          .put(getAPIURL(`/User/Update`), data, config)
          .then((res) => res.data);
      } else {
        return axiosInstance
          .post(getAPIURL("/User/Create"), data, config)
          .then((res) => res.data);
      }
    },
  });
};
